import { createContext, useContext, useState, useCallback, useRef, type ReactNode } from 'react';
import { CheckCircle, XCircle, Info, X } from 'lucide-react';
import { clsx } from 'clsx';

type ToastType = 'success' | 'error' | 'info';

interface ToastAction {
  label: string;
  onClick: () => void;
}

interface Toast {
  id: number;
  type: ToastType;
  message: string;
  action?: ToastAction;
}

interface ToastContextValue {
  toast: (type: ToastType, message: string, action?: ToastAction) => void;
  success: (message: string, action?: ToastAction) => void;
  error: (message: string, action?: ToastAction) => void;
  info: (message: string, action?: ToastAction) => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

const ICONS: Record<ToastType, typeof CheckCircle> = {
  success: CheckCircle,
  error: XCircle,
  info: Info,
};

const STYLES: Record<ToastType, string> = {
  success: 'bg-green-50 text-green-800 border-green-200',
  error: 'bg-red-50 text-red-800 border-red-200',
  info: 'bg-blue-50 text-blue-800 border-blue-200',
};

const ICON_STYLES: Record<ToastType, string> = {
  success: 'text-green-500',
  error: 'text-red-500',
  info: 'text-blue-500',
};

/** Error toasts persist until dismissed. Success/info auto-hide after 5s. */
const AUTO_HIDE_MS: Record<ToastType, number | null> = {
  success: 5000,
  error: null,
  info: 5000,
};

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const idRef = useRef(0);

  const dismiss = useCallback((id: number) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const addToast = useCallback(
    (type: ToastType, message: string, action?: ToastAction) => {
      const id = ++idRef.current;
      setToasts((prev) => [...prev, { id, type, message, action }]);

      const autoHide = AUTO_HIDE_MS[type];
      if (autoHide !== null) {
        setTimeout(() => dismiss(id), autoHide);
      }
    },
    [dismiss],
  );

  const ctx: ToastContextValue = {
    toast: addToast,
    success: (msg, action) => addToast('success', msg, action),
    error: (msg, action) => addToast('error', msg, action),
    info: (msg, action) => addToast('info', msg, action),
  };

  return (
    <ToastContext.Provider value={ctx}>
      {children}

      {/* Toast container */}
      <div className="fixed top-4 right-4 z-50 flex flex-col gap-2 pointer-events-none">
        {toasts.map((t) => {
          const Icon = ICONS[t.type];
          return (
            <div
              key={t.id}
              className={clsx(
                'pointer-events-auto flex items-start gap-2 px-4 py-3 rounded-lg shadow-lg border min-w-[300px] max-w-[480px]',
                STYLES[t.type],
              )}
            >
              <Icon className={clsx('h-5 w-5 shrink-0 mt-0.5', ICON_STYLES[t.type])} />
              <span className="flex-1 text-sm">{t.message}</span>
              {t.action && (
                <button
                  onClick={() => {
                    t.action!.onClick();
                    dismiss(t.id);
                  }}
                  className="shrink-0 text-sm font-medium underline hover:no-underline"
                >
                  {t.action.label}
                </button>
              )}
              <button
                onClick={() => dismiss(t.id)}
                className="shrink-0 text-current opacity-60 hover:opacity-100"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          );
        })}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast(): ToastContextValue {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used within ToastProvider');
  return ctx;
}
